// src/utils/wizardFormats.js
//
// Bridges the wizard's option lists to the plan-entitlement matcher.
//
// The wire pair a wizard option will produce is NEVER transcribed here: every
// answer goes through `resolvePlatformAndFormat` (services/api/backendClient.js),
// the same choke point `buildBriefRequest` uses. That is deliberate — the FE
// gates the picker on `(platform, content_format)`, and if this file kept its own
// copy of the mapping table the two would drift the first time a mode is added
// and the wizard would start offering a format the server refuses (or locking one
// it sells). Add a mode to `resolvePlatformAndFormat` and the only thing needed
// here is its *variant* list below.
//
// ⚠️ UX pre-flight only. The server re-runs the same rule on the run and answers
// 402 FEATURE_NOT_IN_PLAN; nothing here enforces anything.
//
// ⚠️ A blank `content_format` (the resolver's `default:` branch) now yields NO
// key and is therefore locked on any known list. Correct — it is not in the
// closed vocabulary and the BE answers 422 BRIEF_VALIDATION_FAILED for it. No
// reachable MODES entry produces one.
//
// ⚠️ The resolver's `case-study` (`blog-case-study`) and `ads` (`ads-*`)
// branches are NOT in the backend's ten variants. No MODES entry reaches them
// today, but re-enabling either mode would now grey it.

import { resolvePlatformAndFormat } from '../services/api/backendClient.js';
import { SOCIAL_CHANNELS, EMAIL_CHANNELS } from '../constants/channels.js';
import { ADS_CHANNEL_OPTIONS } from '../constants/options.js';
import { isVariantAllowed } from './entitlements.js';

/**
 * The wire `(platform, content_format)` one concrete wizard selection resolves to.
 *
 * A "selection" is the minimal slice of wizard state that moves the mapping:
 * `{ mode, contentFormat, socialChannel, emailChannel, adsChannel }`. Omitted
 * fields fall through to `resolvePlatformAndFormat`'s own defaults — which are
 * the wizard's defaults too (`post`, `email-classic`, `instagram`,
 * `google-search`), so a bare `{ mode }` describes the option as first clicked.
 *
 * @param {{mode: string, contentFormat?: string, socialChannel?: string,
 *          emailChannel?: string, adsChannel?: string}} selection
 * @returns {{platform: string, content_format: string}}
 */
export function wireShapeForSelection(selection) {
  const { mode, contentFormat, socialChannel, emailChannel, adsChannel } = selection || {};
  return resolvePlatformAndFormat({
    activeMode: mode,
    contentFormat,
    socialChannel,
    emailChannel,
    adsParams: adsChannel ? { channel: adsChannel } : undefined,
  });
}

/**
 * Every concrete selection reachable from one mode card.
 *
 * The sub-lists are the ones Step1Setup actually renders (SOCIAL_CHANNELS,
 * EMAIL_CHANNELS, ADS_CHANNEL_OPTIONS) plus LinkedIn's post/carousel segmented
 * control — retire a channel from a picker and it stops counting here too, which
 * is what we want: a mode is "sold" only if something the user can still click
 * is sold.
 *
 * Modes with no sub-choice (blog-seo, landing, case-study) yield a single
 * `{ mode }`. So does an unknown mode — `resolvePlatformAndFormat`'s `default:`
 * branch returns `content_format: ''`, which `entitlements.js` reads as *absent*
 * and therefore as NO key at all: locked on any known list, exactly as the
 * backend refuses the pair.
 *
 * @param {string} modeId
 * @returns {Array<object>} selections, in picker order
 */
export function modeSelections(modeId) {
  switch (modeId) {
    case 'linkedin-post':
      return [
        { mode: modeId, contentFormat: 'post' },
        { mode: modeId, contentFormat: 'carousel' },
      ];
    case 'email':
      return EMAIL_CHANNELS.map(ch => ({ mode: modeId, emailChannel: ch.id }));
    case 'social':
      return SOCIAL_CHANNELS.map(ch => ({ mode: modeId, socialChannel: ch.id }));
    case 'ads':
      return ADS_CHANNEL_OPTIONS.map(opt => ({ mode: modeId, adsChannel: opt.value }));
    default:
      return [{ mode: modeId }];
  }
}

/**
 * Does the plan sell this one selection?
 * @param {string[]|null|undefined} variants plan variant keys; null/absent = unrestricted
 * @param {object} selection see `wireShapeForSelection`
 */
export function isSelectionAllowed(variants, selection) {
  const { platform, content_format: contentFormat } = wireShapeForSelection(selection);
  return isVariantAllowed(variants, platform, contentFormat);
}

/**
 * Does the plan sell *anything* under this mode card?
 *
 * A mode locks only when every variant behind it is refused: on a plan selling
 * `linkedin-post` + `linkedin-carousel`, `linkedin-post` stays open while
 * `email` locks (neither `email-email-classic` nor `linkedin-newsletter` is
 * sold). `[]` needs no special case — `.some()` over an empty allow-list
 * already yields "locked".
 *
 * @param {string[]|null|undefined} variants plan variant keys; null/absent = unrestricted
 * @param {string} modeId
 */
export function isModeAllowed(variants, modeId) {
  if (variants == null) return true;
  return modeSelections(modeId).some(sel => isSelectionAllowed(variants, sel));
}

/**
 * Does the plan sell what the wizard is currently set to?
 *
 * Takes the FULL wizard state and hands it straight to `resolvePlatformAndFormat`
 * — the very object `buildBriefRequest` will resolve — so this answer cannot
 * disagree with the request that would be sent. Used for the "your current
 * selection is not in the plan" notice, which SURFACES the problem rather than
 * silently rewriting the user's choice.
 *
 * @param {string[]|null|undefined} variants plan variant keys; null/absent = unrestricted
 * @param {object} state wizard state (activeMode, contentFormat, socialChannel, …)
 */
export function isCurrentSelectionAllowed(variants, state) {
  if (variants == null) return true;
  const { platform, content_format: contentFormat } = resolvePlatformAndFormat(state || {});
  return isVariantAllowed(variants, platform, contentFormat);
}
